import { EVoiceSessionStatus } from '@constants';
import {
  ISessionProgressPayload,
  IStudySession,
  IInteractiveQuestion,
  IQuestionAnswerPayload
} from '@sdk/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { Room, RoomEvent, Track, RemoteTrack, RemoteParticipant } from 'livekit-client';
import { create as createStore } from 'zustand';

import { postHogService } from '@/services';
import { sdk } from '@/services';
import { ESurveys } from '@/types';

const VoiceService = sdk.getService('VoiceService');

interface IVoiceStore {
  session: IStudySession | null;
  sessionStatus: EVoiceSessionStatus;
  error: string | null;

  room: Room | null;
  token: string | null;
  url: string | null;

  isAgentSpeaking: boolean;
  isMicEnabled: boolean;
  audioLevel: number;

  progress: ISessionProgressPayload | null;
  activeQuestion: IInteractiveQuestion | null;
  lastQuestionResult: { isCorrect: boolean; correctOptionId: string; explanation?: string } | null;

  setSession: (session: IStudySession | null) => void;
  setSessionStatus: (status: EVoiceSessionStatus) => void;
  setError: (error: string | null) => void;
  setRoom: (room: Room | null) => void;
  setToken: (token: string | null) => void;
  setUrl: (url: string | null) => void;
  setIsAgentSpeaking: (speaking: boolean) => void;
  setIsMicEnabled: (enabled: boolean) => void;
  setAudioLevel: (level: number) => void;
  setProgress: (progress: ISessionProgressPayload | null) => void;
  setActiveQuestion: (question: IInteractiveQuestion | null) => void;
  setLastQuestionResult: (
    result: { isCorrect: boolean; correctOptionId: string; explanation?: string } | null
  ) => void;
  reset: () => void;
}

const INITIAL_STATE = {
  session: null,
  sessionStatus: EVoiceSessionStatus.IDLE,
  error: null,
  room: null,
  token: null,
  url: null,
  isAgentSpeaking: false,
  isMicEnabled: true,
  audioLevel: 0,
  progress: null,
  activeQuestion: null,
  lastQuestionResult: null
};

const store = createStore<IVoiceStore>(set => ({
  ...INITIAL_STATE,
  setSession: session => set({ session }),
  setSessionStatus: sessionStatus => set({ sessionStatus }),
  setError: error => set({ error }),
  setRoom: room => set({ room }),
  setToken: token => set({ token }),
  setUrl: url => set({ url }),
  setIsAgentSpeaking: isAgentSpeaking => set({ isAgentSpeaking }),
  setIsMicEnabled: isMicEnabled => set({ isMicEnabled }),
  setAudioLevel: audioLevel => set({ audioLevel }),
  setProgress: progress => set({ progress }),
  setActiveQuestion: activeQuestion => set({ activeQuestion, lastQuestionResult: null }),
  setLastQuestionResult: lastQuestionResult => set({ lastQuestionResult }),
  reset: () => set(INITIAL_STATE)
}));

export const useVoiceSession = () => {
  const {
    session,
    sessionStatus,
    error,
    room,
    isAgentSpeaking,
    isMicEnabled,
    audioLevel,
    progress,
    activeQuestion,
    lastQuestionResult,
    setSession,
    setSessionStatus,
    setError,
    setRoom,
    setToken,
    setUrl,
    setIsAgentSpeaking,
    setIsMicEnabled,
    setProgress,
    setActiveQuestion,
    setLastQuestionResult,
    reset
  } = store();
  const queryClient = useQueryClient();

  const startSession = async (activityId: string, userName: string) => {
    try {
      setSessionStatus(EVoiceSessionStatus.CONNECTING);
      setError(null);

      postHogService.capture('voice_session_started', { activityId });

      const response = await VoiceService.createStudySession(activityId, userName);

      setSession(response.session);
      setToken(response.token);
      setUrl(response.url);

      const newRoom = new Room({
        adaptiveStream: true,
        dynacast: true
      });

      setupRoomEventListeners(newRoom);

      await newRoom.connect(response.url, response.token);

      await newRoom.localParticipant.setMicrophoneEnabled(false);
      setIsMicEnabled(false);

      setRoom(newRoom);
      setSessionStatus(EVoiceSessionStatus.PREPARING);
    } catch (err) {
      console.error('Error starting voice session:', err);
      setError(err instanceof Error ? err.message : 'Error al iniciar la sesión');
      setSessionStatus(EVoiceSessionStatus.ERROR);
    }
  };

  const setupRoomEventListeners = (roomToSetup: Room) => {
    roomToSetup.on(
      RoomEvent.TrackSubscribed,
      (track: RemoteTrack, _publication, participant: RemoteParticipant) => {
        if (track.kind === Track.Kind.Audio) {
          setIsAgentSpeaking(true);
          const audioElement = track.attach();
          audioElement.id = `agent-audio-${participant.identity}`;
          document.body.appendChild(audioElement);
        }
      }
    );

    roomToSetup.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
      if (track.kind === Track.Kind.Audio) {
        setIsAgentSpeaking(false);
        track.detach().forEach(el => el.remove());
      }
    });

    roomToSetup.on(RoomEvent.ActiveSpeakersChanged, speakers => {
      const agentSpeaking = speakers.some(s => s.identity.startsWith('agent'));
      setIsAgentSpeaking(agentSpeaking);
    });

    roomToSetup.on(RoomEvent.DataReceived, async (payload: Uint8Array) => {
      try {
        const decoder = new TextDecoder();
        const data = JSON.parse(decoder.decode(payload));

        if (data.type === 'session:progress' && data.payload) {
          setProgress(data.payload as ISessionProgressPayload);
        } else if (data.type === 'session:ready') {
          await roomToSetup.localParticipant.setMicrophoneEnabled(true);
          setIsMicEnabled(true);
          setSessionStatus(EVoiceSessionStatus.ACTIVE);
        } else if (data.type === 'question:show' && data.payload) {
          setActiveQuestion(data.payload as IInteractiveQuestion);
        } else if (data.type === 'question:result' && data.payload) {
          setLastQuestionResult({
            isCorrect: data.payload.isCorrect,
            correctOptionId: data.payload.correctOptionId,
            explanation: data.payload.explanation
          });
        } else if (data.type === 'question:dismiss') {
          setActiveQuestion(null);
          setLastQuestionResult(null);
        }
      } catch (err) {
        console.error('[VoiceSession] Failed to parse DataChannel message:', err);
      }
    });

    roomToSetup.on(RoomEvent.Disconnected, () => {
      setSessionStatus(EVoiceSessionStatus.ENDED);
    });

    roomToSetup.on(RoomEvent.Reconnecting, () => {
      setSessionStatus(EVoiceSessionStatus.CONNECTING);
    });

    roomToSetup.on(RoomEvent.Reconnected, () => {
      setSessionStatus(EVoiceSessionStatus.CONNECTED);
    });
  };

  const toggleMicrophone = async () => {
    if (!room) return;

    const newState = !isMicEnabled;
    await room.localParticipant.setMicrophoneEnabled(newState);
    setIsMicEnabled(newState);
  };

  const endSession = async () => {
    try {
      if (session?._id) {
        await VoiceService.endStudySession(session._id);
      }

      if (room) await room.disconnect();

      await queryClient.invalidateQueries({ queryKey: ['activity-chat', session?.activity] });
      setSessionStatus(EVoiceSessionStatus.ENDED);

      postHogService.getSurveys(surveys => {
        const survey = surveys.find(s => s.id === ESurveys.VOICE_AGENT);
        if (survey) {
          postHogService.displaySurvey(ESurveys.VOICE_AGENT);
        } else {
          console.error('Survey not found. Looking for ID:', ESurveys.VOICE_AGENT);
        }
      });
    } catch (err) {
      console.error('Error ending session:', err);
    } finally {
      document.querySelectorAll('[id^="agent-audio-"]').forEach(el => el.remove());
      reset();
    }
  };

  const pauseSession = async () => {
    if (!room) return;
    await room.localParticipant.setMicrophoneEnabled(false);
    setIsMicEnabled(false);
    setSessionStatus(EVoiceSessionStatus.PAUSED);
  };

  const resumeSession = async () => {
    if (!room) return;
    await room.localParticipant.setMicrophoneEnabled(true);
    setIsMicEnabled(true);
    setSessionStatus(EVoiceSessionStatus.ACTIVE);
  };

  const answerQuestion = async (questionId: string, selectedOptionId: string) => {
    if (!room) return;

    const answer: IQuestionAnswerPayload = {
      questionId,
      selectedOptionId,
      answeredVia: 'click'
    };

    const encoder = new TextEncoder();
    const data = encoder.encode(
      JSON.stringify({
        type: 'question:answer',
        payload: answer,
        timestamp: new Date().toISOString()
      })
    );

    await room.localParticipant.publishData(data, { reliable: true });
  };

  const dismissQuestion = () => {
    setActiveQuestion(null);
    setLastQuestionResult(null);
  };

  return {
    session,
    sessionStatus,
    error,
    room,
    isAgentSpeaking,
    isMicEnabled,
    audioLevel,
    progress,
    activeQuestion,
    lastQuestionResult,

    isConnected:
      sessionStatus === EVoiceSessionStatus.CONNECTED ||
      sessionStatus === EVoiceSessionStatus.PREPARING ||
      sessionStatus === EVoiceSessionStatus.ACTIVE,
    isLoading:
      sessionStatus === EVoiceSessionStatus.CONNECTING ||
      sessionStatus === EVoiceSessionStatus.PREPARING,

    startSession,
    endSession,
    pauseSession,
    resumeSession,
    toggleMicrophone,
    answerQuestion,
    dismissQuestion,
    reset
  };
};
